import React, { FC } from "react";
import { Helmet } from "react-helmet";
import SocialsList from "shared/SocialsList/SocialsList";
import Label from "components/Label/Label";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import axios from 'axios';
import { BACKEND_URL } from "application/api/apiSlice";
import { API_PREFIX } from "data/constants";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useTurnstile } from "application/hooks/useTurnstile";

export interface PageContactProps {
  className?: string;
}


const PageContact: FC<PageContactProps> = ({ className = "" }) => {
  const { t, ready } = useTranslation("translation");
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [isSending, setIsSending] = React.useState(false);
  const { captchaRef, getToken } = useTurnstile("contact_form");

  const sendSupportEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name || !email || !message) {
      toast.error('Compila tutti i campi per inviare il messaggio');
      return;
    }

    let captchaToken: string | null;
    try {
      captchaToken = await getToken();
    } catch (error) {
      console.error("Turnstile verification failed:", error);
      toast.error(t("translation:captcha.error"));
      return;
    }

    setIsSending(true);
    try {
      await axios.post(`${BACKEND_URL}${API_PREFIX}/email/support`, {
        name,
        email,
        message,
        captcha_token: captchaToken
      });
      toast.success('Messaggio inviato con successo');
    } catch (error) {
      console.error("Error sending support email:", error);
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setIsSending(false);
    }
  }

  if (!ready) { return null; };

  const info = t("translation:contacts.addresses", { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;

  return (
    <div
      className={`nc-PageContact overflow-hidden ${className}`}
      data-nc-id="PageContact"
    >
      <Helmet>
        <title>{t("translation:contacts.title")} || TRIBYOU</title>
      </Helmet>
      <div className="mb-24 lg:mb-32">
        <h2 className="my-16 sm:my-20 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
          {t("translation:contacts.title")}
        </h2>
        <div className="container max-w-7xl mx-auto">
          <div className="flex-shrink-0 grid grid-cols-1 sm:grid-cols-2 gap-12 ">
            <div className="max-w-sm space-y-8">
              {info.map((item, index) => (
                <div key={index}>
                  <h3 className="uppercase font-semibold text-sm dark:text-neutral-200 tracking-wider">
                    {item.title}
                  </h3>
                  <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
                    {item.description}
                  </span>
                </div>
              ))}
              <div>
                <h3 className="uppercase font-semibold text-sm dark:text-neutral-200 tracking-wider">
                  🌏 {t("translation:contacts.socials.title")}
                </h3>
                <SocialsList className="mt-2" />
              </div>
            </div>
            <div>
              <form className="grid grid-cols-1 gap-6" onSubmit={sendSupportEmail}>
                <label className="block">
                  <Label>{t("translation:contacts.form.name")}</Label>

                  <Input
                    placeholder="John Doe"
                    type="text"
                    name="name"
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                    className="mt-1"
                  />
                </label>
                <label className="block">
                  <Label>{t("translation:contacts.form.email")}</Label>

                  <Input
                    type="email"
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                    name="email"
                    placeholder="example@example.com"
                    className="mt-1"
                  />
                </label>
                <label className="block">
                  <Label>{t("translation:contacts.form.message")}</Label>

                  <Textarea
                    className="mt-1"
                    rows={6}
                    name="message"
                    onChange={(e) => {
                      setMessage(e.target.value);
                    }}
                  />
                </label>
                {/* Turnstile renders here only when a challenge is required */}
                <div ref={captchaRef} />
                <div>
                  <ButtonPrimary type="submit" disabled={isSending} loading={isSending}>
                    {t("translation:contacts.form.submit")}
                  </ButtonPrimary>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageContact;
